import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mic, MoreHorizontal, Pencil, Sparkles, UserRound, Users } from "lucide-react";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/PageHeader";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { AugmentPanel } from "@/shared/ui/augment-panel";
import { SlotMaterialsSection } from "@/shared/ui/slot-materials";
import { toast } from "@/shared/hooks/use-toast";
import {
  CHARACTER_SLOTS,
  DH_SOURCE_SLOT,
  SLOT_META,
  characterService,
  type CharacterDto,
  type CharacterMaterialDto,
  type CharacterSlot,
} from "@/entities/character/api/characterService";
import { dhProfileService } from "@/entities/dh-profile/api/dhProfileService";
import { SKU } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { CHARACTER_DELETE_DESCRIPTION } from "../lib/characterDelete";
import { CharacterFormDialog } from "./CharacterFormDialog";
import { errorText, errorToast } from "@/shared/lib/apiErrors";
import { dhSourceVideoError, isAvatarVideo } from "@/entities/dh-profile/model/dhUploadLimits";
import { useDhSourceLimits } from "@/entities/dh-profile/model/useDhSourceLimits";

/**
 * 人物详情页(packs W2 T3;0915 归一批 A 重排)。两块上下排:
 * ①「这个人是谁」角色卡(文字设定 + 声音回显,可编辑)
 * ②「这个人长什么样」四槽素材网格(槽内拖拽排序 / 传 / 删 / 钉主定妆图 / label 编辑 / 迁槽)
 *
 * ★原「真人原料」Tab 与它的 `realAssetsPanel` 路由级注入槽(T5)已退役 —— 那个 Tab 把整个
 * 素材库页原样嵌进来、且不按人物过滤,在人物页列出的是当前登录人名下全部素材,与本人物无关
 * (老板 0915 走查)。归一后**素材长在人物名下,人物库就是素材库** ——
 * 旧的 `/ai-digital-human/avatars` 只剩一条指回 `/characters` 的重定向,接老书签用。
 */
export default function CharacterDetailPage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [augmentOpen, setAugmentOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  /**
   * 素材介质筛选(0916 老板「是否应该直接给两个 tab,照片/视频」)。
   *
   * ★做成**筛选 chips 而不是两个平级 Tab**:身份视频封顶 1 条(MAX_IDENTITY_VIDEOS,
   * 上游 reference_video 槽位与时长墙约束),照片最多 3 张进出片清单、20 张进槽 ——
   * 一个 Tab 装 1 条、另一个装全部,分量对不上;而且 Tab 会把"这个人一共有什么"切碎,
   * 而打开人物页最常见的动作恰恰是"看一眼素材齐不齐",那要一眼看全。
   * 介质维度保留(你要的那个),形式换成筛选条。
   */
  const [mediaFilter, setMediaFilter] = useState<"all" | "image" | "video">("all");

  /**
   * 首张素材入库后自动钉主素材(从零生成流)。
   * ★没有主素材,`standardize` / `slots` 两条 i2i 路在后端会直接报「还没有主定妆图」——
   * 生成完却接不上下一步,用户看到的就是"AI 生成完还是不能用"。
   */
  const pinFirstMaterial = useMutation({
    mutationFn: (materialId: number) => characterService.setPrimaryMaterial(id, materialId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["characters"] }),
  });
  // 0903:素材 Pack AI 补足专价(每张),从价格面取 —— 取不到价面板不显示价格徽章,不许编数。
  const { book } = usePriceBook();
  const augmentUnitCredits = book.unit(SKU.packAugment, "default");

  const detailQuery = useQuery({
    queryKey: ["characters", id],
    queryFn: () => characterService.get(id),
    enabled: Number.isFinite(id),
  });
  const character = detailQuery.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["characters"] });
  };

  const removeCharacter = useMutation({
    mutationFn: () => characterService.remove(id),
    onSuccess: () => {
      /**
       * ★删完必须先把这个人物的缓存**摘掉**,再刷列表(2026-08-14 四天反馈校对 TOP#9)。
       *
       * 爱民 0812「删除了为啥提醒这个,感觉不对劲」+ 截图「数据加载失败 / 人物不存在:
       * 5019916567553」—— 根因不是列表没失效(列表是对的,卡确实没了),是
       * `invalidateQueries(["characters"])` 前缀匹配**连 `["characters", id]` 这条详情查询
       * 一起标脏**;而此刻详情页还挂着,react-query 立刻重拉一次刚被删掉的人物 → 404 →
       * 全局 onError 弹「数据加载失败」。真机实测这一下连弹十几条,把「已删除人物」压没了:
       * 用户删成功了,却收到一屏报错。
       *
       * removeQueries 用前缀是故意的:物料列表挂在 ["characters", id, ...] 下,一并摘干净,
       * 否则换个 tab 回来又是一轮 404。列表那条用 exact 刷,才不会误伤别的实体。
       */
      queryClient.removeQueries({ queryKey: ["characters", id] });
      queryClient.invalidateQueries({ queryKey: ["characters"], exact: true });
      toast({ title: "已删除人物", description: "人物与全部物料已移除。" });
      navigate("/characters");
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "删除失败" })),
  });

  const toggleStatus = useMutation({
    mutationFn: (c: CharacterDto) =>
      characterService.update(c.id, {
        name: c.name,
        status: c.status === "active" ? "disabled" : "active",
      }),
    onSuccess: (saved) => {
      invalidate();
      toast({
        title: saved.status === "active" ? "已启用" : "已停用",
        description:
          saved.status === "active"
            ? "人物重新对创作可用。"
            : "停用后创作侧不再选到该人物,物料保留。",
      });
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "操作失败" })),
  });

  if (!Number.isFinite(id)) {
    return <QueryErrorBanner isError message="无效的人物 id" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        /**
         * ★0915 归一批 A:返回接回全站唯一实现。此前这一页**没传 backTo**,自己在右侧
         * actions 里画了一颗「← 返回人物库」——两个毛病叠出老板那句「点进一个人物之后没有
         * 返回按钮」:① 位置错(全站返回都在页头最左,肌肉记忆往左上角找,那里什么都没有);
         * ② 目标错(硬编码 navigate("/characters"),从真人出镜页/视频创建页选人进来的,返回
         * 被扔回人物库而不是回原页)。`useBackNav` 的三层兜底(来源路由 → 站内历史 → 兜底
         * 路径)正是为②写的,这页一直没接上。
         */
        backTo="/characters"
        icon={character?.isRealPerson ? UserRound : Users}
        title={character?.name ?? "人物详情"}
        subtitle={
          character ? (
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{character.isRealPerson ? "真人" : "虚拟人"}</Badge>
              {character.status === "disabled" && <Badge variant="outline">停用</Badge>}
              <AssetsReadyBadge character={character} />
              {/* ★外观一句话(0916 从角色卡里提出来):它是唯一真进提示词链的那一项 ——
                  提交编排时 withCharacterSheet 把它逐字拼成【主角人物卡】。放页头一行,
                  和名字/类型/声音并列,不再埋在一张几乎全空的卡里。 */}
              {character.appearance ? (
                <span className="truncate">{character.appearance}</span>
              ) : (
                <span className="text-muted-foreground/70">未填外观</span>
              )}
              <VoiceBindingLine character={character} />
            </span>
          ) : undefined
        }
        actions={
          <div className="flex gap-2">
            {character && (
              <>
                <Button variant="outline" onClick={() => setEditOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  编辑信息
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="更多操作">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => toggleStatus.mutate(character)}>
                      {character.status === "active" ? "停用人物" : "启用人物"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteOpen(true)}
                    >
                      删除人物
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        }
      />

      <QueryErrorBanner
        isError={detailQuery.isError}
        message={detailQuery.error ? errorText(detailQuery.error) : undefined}
        onRetry={() => detailQuery.refetch()}
      />

      {detailQuery.isLoading ? (
        <div className="space-y-4" data-testid="character-detail-skeleton">
          <div className="fx-skeleton h-40 rounded-[calc(var(--radius)*1.5)] border border-border/50 bg-card/40" />
          <div className="fx-skeleton h-64 rounded-[calc(var(--radius)*1.5)] border border-border/50 bg-card/40" />
        </div>
      ) : character ? (
        /**
         * ★0915 归一批 A:三个 Tab 塌成两块;0916 再收一刀,只剩**一块**。
         *
         * 「这个人是谁」那块(角色卡)整块退役 —— 0916 查证:12 字段 `personaJson`
         * **零消费面**(realPersonCast 不带、entities 不带、VideoTaskCreateRequest 根本没这字段,
         * api 只有 CRUD、console 只把它显示回给填它的人),两台机器 10 个人物**0 个填过**。
         * 页面上「角色卡(进提示词链)」这句话不成立。
         * 唯一真在用的是 `appearance` 那一句(提交编排时 withCharacterSheet 逐字拼进 userInput),
         * 它值一行,不值一张卡 —— 已提到页头副标,与类型/声音/就绪态并列。
         */
        <MaterialBoard
          character={character}
          onAugment={() => setAugmentOpen(true)}
          mediaFilter={mediaFilter}
          onMediaFilter={setMediaFilter}
        />
      ) : null}

      {character && (
        <CharacterFormDialog open={editOpen} onOpenChange={setEditOpen} character={character} />
      )}

      {character && (
        <AugmentPanel
          open={augmentOpen}
          onOpenChange={setAugmentOpen}
          title={`AI 补足 · ${character.name}`}
          modes={[
            /**
             * ★从零生成只给**虚拟人物 + 名下还没有素材**这一格(0916)。
             * ① 真人:后端硬拒(凭一句描述"生成"一个真实存在的人 = 绕开肖像授权存证整条链),
             *    前端不画这颗按钮,免得点了才吃一个 400;
             * ② 已经有素材的虚拟人物:该走 i2i(定妆标准化 / 按槽批量)—— 再文生图一次
             *    出来的是**另一个人**,恰好把老板要的"保证人物一致性"打掉。
             *    一致性靠的就是"只从零生成一次,之后全部以它为锚"。
             */
            ...(!character.isRealPerson && (character.materials ?? []).length === 0
              ? [
                  {
                    key: "create",
                    label: "从零生成",
                    hint: "写一句这个人长什么样,AI 出 3 张定妆照,挑 1 张作为这个人的底",
                    briefPlaceholder:
                      "如:25 岁女性动漫角色,黑色齐肩短发,浅蓝色眼睛,白衬衫配深蓝针织背心,干净清爽的二次元画风",
                    estimatedCount: 3,
                  },
                ]
              : []),
            {
              key: "standardize",
              label: "定妆标准化",
              hint: "以主定妆图(或原照)出 3 张标准定妆照,挑 1 张入定妆槽",
              estimatedCount: 3,
            },
            {
              key: "slots",
              label: "按槽批量生成",
              hint: "从主定妆图按槽批量出图(全跑约 20 张);大角度可能漂脸,入库前人眼挑",
              slotOptions: CHARACTER_SLOTS.map((s) => ({ key: s, label: SLOT_META[s].label })),
              // 全跑(4 槽)约 20 张 = 每槽 5 张(hint 里的 20 就是这么来的,这里不重发明)
              estimatedCount: 5,
            },
          ]}
          slotLabels={Object.fromEntries(CHARACTER_SLOTS.map((s) => [s, SLOT_META[s].label]))}
          queryKeyBase={["characters", character.id]}
          unitCredits={augmentUnitCredits}
          adapter={{
            start: (mode, slots, brief) =>
              characterService.augment(character.id, { mode, slots, brief }),
            fetchTask: (taskId) => characterService.augmentTask(character.id, taskId),
            curate: (taskId, picks) => characterService.curateFromTask(character.id, taskId, picks),
            // 面板一关也找得回:最近几单摆在选模式态顶上(0916 补挂账)。
            listTasks: () => characterService.augmentTasks(character.id),
          }}
          onCurated={(created) => {
            // 从零生成的第一张就是这个人物的「底」—— 自动钉成主素材,不让用户再去找一次菜单。
            // (后续补足以它为 i2i 锚,不钉的话 standardize/slots 两条路都会报「还没有主定妆图」。)
            if (character.primaryMaterialId == null && created.length > 0) {
              pinFirstMaterial.mutate(created[0].id);
            }
          }}
        />
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除人物「{character?.name}」?</AlertDialogTitle>
            <AlertDialogDescription>{CHARACTER_DELETE_DESCRIPTION}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => removeCharacter.mutate()}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * 厂商素材就绪态徽标(0915 归一批 A)。
 *
 * ★为什么提到人物页头:这个态此前**只在出片页**才露出来 —— 素材没注册好,用户走到出片
 * 才看见「素材准备中」(0915 九宫格 P0 就卡在这个形态上,而且当时失败既不落库也不打日志)。
 * 人物页是最该看见它的地方:进来就知道这个人现在能不能用。
 *
 * ★只在**真人 + 非 active** 时出徽标:`verification.assets` 对虚拟人物恒 `active`
 * (不消费厂商素材,不存在"未就绪"这个概念),给每个虚拟人物挂一枚绿标是零信息的噪声。
 * 徽标出现即有事 —— 这是它的信息价值所在。
 */
function AssetsReadyBadge({ character }: { character: CharacterDto }) {
  if (!character.isRealPerson) return null;
  const assets = character.verification?.assets;
  if (!assets || assets === "active") return null;
  if (assets === "registering") {
    return (
      <Badge variant="outline" className="text-warning">
        素材准备中
      </Badge>
    );
  }
  if (assets === "none") return <Badge variant="outline">未传素材</Badge>;
  // rejected / failed —— 两者对用户都是「这个人现在出不了片」,措辞不分家。
  return <Badge variant="destructive">素材注册失败</Badge>;
}

/**
 * 声音绑定回显(0916:从退役的角色卡里保留下来的那一行)。
 *
 * <p>★为什么它必须显示**真实声纹名**:0915 现网事故 —— 人物「阿杰」绑的声纹叫「彭姐」、
 * 实测 F0 207.8Hz 是女声,而页面上完全看不出绑的是哪一条。人物页是最该看见它的地方。
 */
function VoiceBindingLine({ character }: { character: CharacterDto }) {
  const boundPreset = character.presetVoice ?? null;
  const boundProfileId = character.voiceProfileId ?? null;
  const presetVoicesQuery = useQuery({
    queryKey: ["dh", "preset-voices"],
    queryFn: () => dhProfileService.listPresetVoices(),
    enabled: !!boundPreset,
    staleTime: 5 * 60 * 1000,
  });
  const voiceProfilesQuery = useQuery({
    queryKey: ["dh", "voice-profiles"],
    queryFn: () => dhProfileService.listVoiceProfiles(),
    enabled: !!boundProfileId,
    staleTime: 60 * 1000,
  });
  if (!boundProfileId && !boundPreset) {
    return <span className="text-muted-foreground/70">未绑声音</span>;
  }
  const label = boundProfileId
    ? (voiceProfilesQuery.data?.profiles.find((p) => p.id === boundProfileId)?.label ??
      `#${boundProfileId}`)
    : (presetVoicesQuery.data?.find((v) => v.id === boundPreset)?.label ?? boundPreset);
  return (
    <span className="inline-flex items-center gap-1">
      <Mic className="h-3.5 w-3.5 opacity-60" />
      {label}
    </span>
  );
}

/** 介质筛选一档。 */
type MediaFilter = "all" | "image" | "video";

/**
 * 素材台(0916 重排:四个上下堆叠的槽分区 → **一个网格**)。
 *
 * <p>★为什么不再按槽分区:老板 0916「素材库的人物页面排版不合理」。gstack 走查实测的三条病：
 * ① 一个人物页面上 <b>5 个上传按钮</b>(四槽各一 + AI 补足),同一个动作五个入口;
 * ② 常态是只有定妆槽有货,页面 <b>3/4 的纵向面积是三条「这个槽还空着」的虚线框</b> ——
 *    要看第二张素材得先滚过两屏空白;
 * ③ 窄屏下上传按钮把槽标题挤成一指宽。
 * 槽位没有消失,它降级成卡左下角的一枚角标 + 卡菜单里的「移到…」—— 它本来就只是标注,
 * 出片时取哪几张由 {@code CharacterOwnRefs}(主定妆图必选 + updated_at 补足)算,不看槽。
 *
 * <p>★视频排最前:一个人物最多一条身份视频,它是最贵也最关键的那条素材,
 * 排在后面等于藏起来(老板 0915「只有视频他无法上传」那条线的正面兑现)。
 *
 * <p>★跨槽拖拽排序<b>不给</b>:后端 reorder 契约是「同槽内全覆盖」,平铺网格里拖出来的
 * 顺序跨槽无处落。改顺序的唯一真实需求是"哪张当主图",那由「设为主定妆图」直给。
 */
function MaterialBoard({
  character,
  onAugment,
  mediaFilter,
  onMediaFilter,
}: {
  character: CharacterDto;
  onAugment: () => void;
  mediaFilter: MediaFilter;
  onMediaFilter: (f: MediaFilter) => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<CharacterMaterialDto | null>(null);
  const [deleting, setDeleting] = useState<CharacterMaterialDto | null>(null);
  const [uploading, setUploading] = useState(false);
  const dhSourceLimits = useDhSourceLimits();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["characters"] });
  };

  // ★1006 两区:分身口播素材(dh_source)单列,不进真人出镜身份素材区的计数/筛选/排序。
  const allMaterials = character.materials ?? [];
  const all = allMaterials.filter((m) => m.slot !== DH_SOURCE_SLOT);
  const dhSource = allMaterials.filter((m) => m.slot === DH_SOURCE_SLOT);
  const counts = {
    all: all.length,
    image: all.filter((m) => m.kind !== "video").length,
    video: all.filter((m) => m.kind === "video").length,
  };

  /**
   * 展示序:视频 → 主定妆图 → 其余按槽序、槽内按 sortWeight。
   * 纯展示口径,不落库(落库序只在槽内有意义,见头注)。
   */
  const ordered = [...all].sort((a, b) => {
    const rank = (m: CharacterMaterialDto) =>
      m.kind === "video" ? 0 : m.id === character.primaryMaterialId ? 1 : 2;
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    const slotDelta = CHARACTER_SLOTS.indexOf(a.slot) - CHARACTER_SLOTS.indexOf(b.slot);
    if (slotDelta !== 0) return slotDelta;
    return (a.sortWeight ?? 0) - (b.sortWeight ?? 0) || a.id - b.id;
  });
  const shown = ordered.filter((m) =>
    mediaFilter === "all" ? true : mediaFilter === "video" ? m.kind === "video" : m.kind !== "video"
  );

  const upload = async (files: FileList, slot: CharacterSlot = "identity") => {
    if (!files.length) return;
    setUploading(true);
    try {
      // 1030:分身区的视频先过前置体检(与分身创建页同一函数),不合格整批不传 —— 出不了片的素材别进库。
      if (slot === DH_SOURCE_SLOT) {
        for (const file of Array.from(files)) {
          if (!isAvatarVideo(file)) continue;
          const err = await dhSourceVideoError(file, dhSourceLimits);
          if (err) {
            toast({
              title: `「${file.name}」不能用于分身`,
              description: err,
              variant: "destructive",
            });
            return;
          }
        }
      }
      // 顺序传不并发:后端按插入序给 sort_weight,并发会乱序;逐张报错也更可读。
      // 身份区落 identity 槽:它是唯一同时收图与视频的槽,也是出片清单的锚;
      // 想标成表情/姿态/造型,卡菜单「移到…」一步搞定(比先选槽再传少一次决策)。
      // 分身区(1006)落 dh_source 槽:只给分身出片用,不让这个人进真人出镜选人。
      for (const file of Array.from(files)) {
        await characterService.uploadMaterial(character.id, file, slot);
      }
      toast({ title: "已上传", description: `${files.length} 份素材已入库。` });
      invalidate();
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "上传失败" }));
    } finally {
      setUploading(false);
    }
  };

  const moveSlot = useMutation({
    mutationFn: ({ material, to }: { material: CharacterMaterialDto; to: CharacterSlot }) =>
      characterService.updateMaterial(character.id, material.id, { slot: to }),
    onSuccess: (_, { to }) => {
      invalidate();
      toast({ title: "已改槽", description: `素材标为「${SLOT_META[to].label}」。` });
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "改槽失败" })),
  });

  const pinPrimary = useMutation({
    mutationFn: (materialId: number) =>
      characterService.setPrimaryMaterial(character.id, materialId),
    onSuccess: () => {
      invalidate();
      toast({ title: "已设为主素材", description: "库墙卡面与消费端锚图随之更新。" });
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "操作失败" })),
  });

  const removeMaterial = useMutation({
    mutationFn: (materialId: number) => characterService.deleteMaterial(character.id, materialId),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
      toast({ title: "已删除素材" });
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "删除失败" })),
  });

  const byId = (id: number) => allMaterials.find((m) => m.id === id) ?? null;
  // 分身区展示序:视频在前,再按 sortWeight。
  const dhOrdered = [...dhSource].sort(
    (a, b) =>
      (a.kind === "video" ? 0 : 1) - (b.kind === "video" ? 0 : 1) ||
      (a.sortWeight ?? 0) - (b.sortWeight ?? 0) ||
      a.id - b.id
  );

  return (
    <>
      <SlotMaterialsSection
        title="真人出镜身份素材"
        hint="照片与视频同表同槽;真人出镜时主定妆图必选,其余按最近更新补足。传进来默认记在定妆,卡片菜单可改。"
        countUnit="项"
        materials={shown.map((m) => ({
          ...m,
          slotLabel: SLOT_META[m.slot].label,
          slotKey: m.slot,
        }))}
        uploading={uploading}
        acceptVideo
        uploadText="传素材"
        onUpload={(files) => void upload(files)}
        extraActions={
          <Button
            variant="outline"
            size="sm"
            onClick={onAugment}
            /* 真人没素材时 AI 生成无从下手(三个 mode 全要底图,从零生成对真人禁用);
               虚拟人没素材恰恰是「从零生成」唯一的用武之地,不能一起灰掉。 */
            disabled={all.length === 0 && character.isRealPerson}
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            AI 生成
          </Button>
        }
        toolbar={<MediaFilterChips value={mediaFilter} onChange={onMediaFilter} counts={counts} />}
        emptyText={
          all.length === 0
            ? "还没有真人出镜身份素材 —— 传一张照片或一段出镜视频,这个人就能在真人出镜里出片了。"
            : mediaFilter === "video"
              ? "这个人还没有身份视频。传一段 5~15 秒的正面出镜视频,出片时可当身份参考。"
              : "这个人还没有照片。"
        }
        moveTargets={CHARACTER_SLOTS.map((s) => ({ key: s, label: SLOT_META[s].label }))}
        onMoveSlot={(m, key) => {
          const material = byId(m.id);
          if (material) moveSlot.mutate({ material, to: key as CharacterSlot });
        }}
        onEditLabel={(m) => setEditing(byId(m.id))}
        onDelete={(m) => setDeleting(byId(m.id))}
        primaryMaterialId={character.primaryMaterialId}
        canPin
        onPin={(m) => pinPrimary.mutate(m.id)}
        pinBadgeText="主素材"
        pinActionText="设为主素材"
        domainAttr="data-character-board"
      />

      {/* ★1006 分身口播素材区:分身仓上传的视频/照片落 dh_source 槽,只给分身出片用。
          「移到另一区」不给:跨区改槽后端未承诺接受(dh_source ↔ identity 会改变真人出镜
          可选性与入库链),没有确定接口就不造按钮。区内也不出「移到…」(单槽)。 */}
      <div className="mt-6">
        <SlotMaterialsSection
          title="分身口播素材"
          hint="分身仓上传的口播视频/照片 —— 只给分身出片用,不会让这个人出现在真人出镜的选人里。"
          countUnit="项"
          materials={dhOrdered.map((m) => ({
            ...m,
            slotLabel: SLOT_META[m.slot].label,
            slotKey: m.slot,
          }))}
          uploading={uploading}
          acceptVideo
          uploadText="传分身素材"
          onUpload={(files) => void upload(files, DH_SOURCE_SLOT)}
          emptyText="还没有分身口播素材 —— 在分身仓上传的视频/照片会出现在这里,也可以直接在这里传。"
          onEditLabel={(m) => setEditing(byId(m.id))}
          onDelete={(m) => setDeleting(byId(m.id))}
          primaryMaterialId={character.primaryMaterialId}
          canPin
          onPin={(m) => pinPrimary.mutate(m.id)}
          pinBadgeText="主素材"
          pinActionText="设为主素材"
          domainAttr="data-character-dh-board"
        />
      </div>

      <LabelEditDialog
        material={editing}
        onClose={() => setEditing(null)}
        characterId={character.id}
        onSaved={invalidate}
      />

      <AlertDialog open={deleting != null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除这份素材?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.label ? `「${deleting.label}」` : "该素材"}
              将从这个人物名下移除,已出的片不受影响。此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleting && removeMaterial.mutate(deleting.id)}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * 介质筛选 chips(老板 0916「是否应该直接给两个 tab,照片/视频」的落法)。
 *
 * <p>做成筛选条而不是 Tab 的理由见 CharacterDetailPage 里 mediaFilter 的头注:
 * 一条视频 vs 二十张照片,两个平级 Tab 的分量对不上,而且打开人物页第一件事是
 * "看一眼素材齐不齐",Tab 会把这一眼切碎。
 */
function MediaFilterChips({
  value,
  onChange,
  counts,
}: {
  value: MediaFilter;
  onChange: (f: MediaFilter) => void;
  counts: { all: number; image: number; video: number };
}) {
  const chips: { key: MediaFilter; label: string; n: number }[] = [
    { key: "all", label: "全部", n: counts.all },
    { key: "image", label: "照片", n: counts.image },
    { key: "video", label: "视频", n: counts.video },
  ];
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="按介质筛选素材">
      {chips.map((c) => (
        <Button
          key={c.key}
          type="button"
          size="sm"
          variant={value === c.key ? "default" : "outline"}
          aria-pressed={value === c.key}
          data-testid={`media-filter-${c.key}`}
          className="h-7 rounded-full px-3 text-xs"
          onClick={() => onChange(c.key)}
        >
          {c.label}
          <span className="ml-1.5 tabular-nums opacity-70">{c.n}</span>
        </Button>
      ))}
    </div>
  );
}

/** label 编辑小弹窗(PATCH 只动 label)。 */
function LabelEditDialog({
  material,
  characterId,
  onClose,
  onSaved,
}: {
  material: CharacterMaterialDto | null;
  characterId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState("");
  // material 变化时同步初值:受控于「打开哪张」而不是本地状态生命周期。
  const [lastId, setLastId] = useState<number | null>(null);
  if (material && material.id !== lastId) {
    setLastId(material.id);
    setValue(material.label ?? "");
  }

  const save = useMutation({
    mutationFn: (label: string) =>
      characterService.updateMaterial(characterId, material!.id, { label }),
    onSuccess: () => {
      onSaved();
      onClose();
      toast({ title: "已更新标注" });
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "保存失败" })),
  });

  return (
    <Dialog open={material != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>改标注</DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="material-label">标注</Label>
          <Input
            id="material-label"
            value={value}
            placeholder="如:正面 / 微笑 / 手持产品"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save.mutate(value.trim())}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            取消
          </Button>
          <Button onClick={() => save.mutate(value.trim())} disabled={save.isPending}>
            {save.isPending ? "保存中…" : "保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
